import {
  DEFAULT_SOUND_ID,
  getNotificationSound
} from '@/constants/notificationSounds';

/**
 * Разблокировка воспроизведения аудио в обход browser autoplay policy.
 *
 * Браузеры выдают сайту «sticky activation» по origin после первого жеста
 * пользователя — и тогда `audio.play()` работает всегда, в том числе когда
 * вкладка в фоне и окно свёрнуто. Поэтому достаточно один раз на самый первый
 * жест «прогреть» общий аудио-элемент (проиграть без звука), после чего
 * воспроизведение по событию сокета звучит без дополнительных действий.
 *
 * Используется единый аудио-инстанс на всё приложение (`getSharedAudio`),
 * чтобы выданное браузером разрешение относилось именно к нему.
 */

let sharedAudio: HTMLAudioElement | null = null;
let isUnlocked = false;
let isRegistered = false;

const GESTURE_EVENTS: Array<keyof DocumentEventMap> = [
  'pointerdown',
  'keydown',
  'touchstart'
];

/** Возвращает единый аудио-элемент приложения (ленивая инициализация). */
export const getSharedAudio = (): HTMLAudioElement => {
  if (sharedAudio === null) {
    sharedAudio = new Audio();
  }
  return sharedAudio;
};

/** Разблокирован ли уже звук (sticky activation получена). */
export const isAudioUnlocked = (): boolean => isUnlocked;

const removeGestureListeners = (handler: () => void): void => {
  GESTURE_EVENTS.forEach((eventName) => {
    document.removeEventListener(eventName, handler, true);
  });
};

/**
 * Регистрирует однократную разблокировку аудио на первый жест пользователя.
 * Идемпотентна: повторные вызовы игнорируются. Слушатели снимаются после
 * первого успешного жеста.
 */
export const registerAudioUnlock = (): void => {
  if (isRegistered || isUnlocked) {
    return;
  }
  isRegistered = true;

  const unlock = (): void => {
    if (isUnlocked) {
      return;
    }

    const audio = getSharedAudio();

    // Без src play() сразу отклонится — задаём дефолтную мелодию для прогрева
    // (для разрешения origin конкретная мелодия не важна).
    if (audio.src === '') {
      audio.src = getNotificationSound(DEFAULT_SOUND_ID).src;
    }
    audio.muted = true;

    audio
      .play()
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.muted = false;
        isUnlocked = true;
        removeGestureListeners(unlock);
      })
      .catch(() => {
        // Жест не дал разрешения (нет src / политика) — пробуем на следующий.
        audio.muted = false;
      });
  };

  GESTURE_EVENTS.forEach((eventName) => {
    document.addEventListener(eventName, unlock, true);
  });
};
